import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgressData, useClientProfiles } from '../../hooks/useFirestore';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { FaWeight, FaRuler, FaChartLine, FaTrophy, FaFire, FaCalendarAlt } from 'react-icons/fa';
import './ProgressDashboard.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ProgressDashboard = ({ clientId = null }) => {
  const { currentUser, userProfile } = useAuth();
  const userId = clientId || currentUser?.uid;

  const { data: progressEntries, loading } = useProgressData(userId);
  const { data: profiles } = useClientProfiles([userId]);
  const profile = profiles?.[0];

  const [timeRange, setTimeRange] = useState('90'); // 30, 90, 180, 365, all
  const [selectedMetric, setSelectedMetric] = useState('weight'); // weight, bodyFat, measurements

  // Filter entries by time range
  const filteredEntries = useMemo(() => {
    if (!progressEntries.length) return [];

    const sorted = [...progressEntries].sort((a, b) => a.date - b.date);

    if (timeRange === 'all') return sorted;

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - parseInt(timeRange));

    return sorted.filter(entry => entry.date.toDate() >= cutoffDate);
  }, [progressEntries, timeRange]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (filteredEntries.length === 0) {
      return {
        currentWeight: null,
        startWeight: null,
        weightChange: null,
        currentBodyFat: null,
        startBodyFat: null,
        bodyFatChange: null,
        avgWeightChange: null,
        daysTracking: 0,
        totalEntries: 0
      };
    }

    const first = filteredEntries[0];
    const last = filteredEntries[filteredEntries.length - 1];

    const weightChange = last.weight && first.weight ? last.weight - first.weight : null;
    const bodyFatChange = last.bodyFat && first.bodyFat ? last.bodyFat - first.bodyFat : null;

    const daysTracking = Math.floor(
      (last.date.toDate() - first.date.toDate()) / (1000 * 60 * 60 * 24)
    );

    const avgWeightChange = weightChange && daysTracking > 0
      ? (weightChange / daysTracking) * 7 // per week
      : null;

    return {
      currentWeight: last.weight,
      startWeight: first.weight,
      weightChange,
      currentBodyFat: last.bodyFat,
      startBodyFat: first.bodyFat,
      bodyFatChange,
      avgWeightChange,
      daysTracking,
      totalEntries: filteredEntries.length
    };
  }, [filteredEntries]);

  // Prepare weight chart data
  const weightChartData = useMemo(() => {
    const entries = filteredEntries.filter(e => e.weight);

    return {
      labels: entries.map(e => e.date.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })),
      datasets: [
        {
          label: 'Weight (kg)',
          data: entries.map(e => e.weight),
          borderColor: '#ff6b35',
          backgroundColor: 'rgba(255, 107, 53, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        ...(profile?.targetWeight ? [{
          label: 'Target Weight',
          data: Array(entries.length).fill(profile.targetWeight),
          borderColor: '#4ecdc4',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0
        }] : [])
      ]
    };
  }, [filteredEntries, profile]);

  // Prepare body fat chart data
  const bodyFatChartData = useMemo(() => {
    const entries = filteredEntries.filter(e => e.bodyFat);

    return {
      labels: entries.map(e => e.date.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })),
      datasets: [
        {
          label: 'Body Fat (%)',
          data: entries.map(e => e.bodyFat),
          borderColor: '#4ecdc4',
          backgroundColor: 'rgba(78, 205, 196, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  }, [filteredEntries]);

  // Prepare measurements chart data
  const measurementsChartData = useMemo(() => {
    const measurementTypes = ['chest', 'waist', 'hips'];
    const entries = filteredEntries.filter(e =>
      measurementTypes.some(type => e.measurements?.[type])
    );

    return {
      labels: entries.map(e => e.date.toDate().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })),
      datasets: measurementTypes.map((type, index) => ({
        label: type.charAt(0).toUpperCase() + type.slice(1),
        data: entries.map(e => e.measurements?.[type] || null),
        borderColor: ['#ff6b35', '#4ecdc4', '#f4a261'][index],
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5
      }))
    };
  }, [filteredEntries]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: { size: 12 },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(10, 10, 10, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        padding: 12,
        displayColors: true
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
          drawBorder: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: { size: 11 }
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.05)',
          drawBorder: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: { size: 11 }
        }
      }
    }
  };

  // Calculate progress percentage towards goal
  const progressPercentage = useMemo(() => {
    if (!stats.currentWeight || !stats.startWeight || !profile?.targetWeight) return null;

    const totalChange = profile.targetWeight - stats.startWeight;
    const currentChange = stats.currentWeight - stats.startWeight;

    if (totalChange === 0) return 100;

    return Math.min(Math.max((currentChange / totalChange) * 100, 0), 100);
  }, [stats, profile]);

  if (loading) {
    return (
      <div className="progress-dashboard">
        <div className="loading-state">Loading progress data...</div>
      </div>
    );
  }

  return (
    <div className="progress-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1><FaChartLine /> Progress Analytics</h1>
          <p>Visualize your progress over time</p>
        </div>

        <div className="time-range-selector">
          <button
            className={`range-btn ${timeRange === '30' ? 'active' : ''}`}
            onClick={() => setTimeRange('30')}
          >
            30 Days
          </button>
          <button
            className={`range-btn ${timeRange === '90' ? 'active' : ''}`}
            onClick={() => setTimeRange('90')}
          >
            90 Days
          </button>
          <button
            className={`range-btn ${timeRange === '180' ? 'active' : ''}`}
            onClick={() => setTimeRange('180')}
          >
            6 Months
          </button>
          <button
            className={`range-btn ${timeRange === '365' ? 'active' : ''}`}
            onClick={() => setTimeRange('365')}
          >
            1 Year
          </button>
          <button
            className={`range-btn ${timeRange === 'all' ? 'active' : ''}`}
            onClick={() => setTimeRange('all')}
          >
            All Time
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon weight">
            <FaWeight />
          </div>
          <div className="stat-content">
            <div className="stat-label">Current Weight</div>
            <div className="stat-value">
              {stats.currentWeight ? `${stats.currentWeight} kg` : 'N/A'}
            </div>
            {stats.weightChange !== null && (
              <div className={`stat-change ${stats.weightChange < 0 ? 'positive' : 'negative'}`}>
                {stats.weightChange > 0 ? '+' : ''}{stats.weightChange.toFixed(1)} kg
              </div>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bodyfat">
            <FaRuler />
          </div>
          <div className="stat-content">
            <div className="stat-label">Body Fat</div>
            <div className="stat-value">
              {stats.currentBodyFat ? `${stats.currentBodyFat}%` : 'N/A'}
            </div>
            {stats.bodyFatChange !== null && (
              <div className={`stat-change ${stats.bodyFatChange < 0 ? 'positive' : 'negative'}`}>
                {stats.bodyFatChange > 0 ? '+' : ''}{stats.bodyFatChange.toFixed(1)}%
              </div>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon rate">
            <FaFire />
          </div>
          <div className="stat-content">
            <div className="stat-label">Avg Change/Week</div>
            <div className="stat-value">
              {stats.avgWeightChange
                ? `${stats.avgWeightChange > 0 ? '+' : ''}${stats.avgWeightChange.toFixed(2)} kg`
                : 'N/A'}
            </div>
            <div className="stat-subtext">Based on current trend</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon entries">
            <FaCalendarAlt />
          </div>
          <div className="stat-content">
            <div className="stat-label">Tracking Period</div>
            <div className="stat-value">{stats.daysTracking} days</div>
            <div className="stat-subtext">{stats.totalEntries} entries logged</div>
          </div>
        </div>
      </div>

      {/* Goal Progress */}
      {profile?.targetWeight && stats.currentWeight && (
        <div className="goal-progress-card">
          <div className="goal-header">
            <h3><FaTrophy /> Goal Progress</h3>
            <div className="goal-info">
              <span className="start-weight">{stats.startWeight} kg</span>
              <span className="arrow">→</span>
              <span className="target-weight">{profile.targetWeight} kg</span>
            </div>
          </div>

          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progressPercentage}%` }}
            />
            <div className="progress-percentage">{progressPercentage?.toFixed(0)}%</div>
          </div>

          <div className="goal-stats">
            <div className="goal-stat">
              <span className="label">Current</span>
              <span className="value">{stats.currentWeight} kg</span>
            </div>
            <div className="goal-stat">
              <span className="label">Remaining</span>
              <span className="value">
                {Math.abs(profile.targetWeight - stats.currentWeight).toFixed(1)} kg
              </span>
            </div>
            <div className="goal-stat">
              <span className="label">Total Change</span>
              <span className="value">
                {stats.weightChange > 0 ? '+' : ''}{stats.weightChange?.toFixed(1)} kg
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {filteredEntries.length > 0 ? (
        <div className="charts-container">
          {/* Weight Chart */}
          {filteredEntries.some(e => e.weight) && (
            <div className="chart-card">
              <div className="chart-header">
                <h3>Weight Trend</h3>
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#ff6b35' }}></span>
                    Actual Weight
                  </span>
                  {profile?.targetWeight && (
                    <span className="legend-item">
                      <span className="legend-dot dashed" style={{ borderColor: '#4ecdc4' }}></span>
                      Target Weight
                    </span>
                  )}
                </div>
              </div>
              <div className="chart-wrapper">
                <Line data={weightChartData} options={chartOptions} />
              </div>
            </div>
          )}

          {/* Body Fat Chart */}
          {filteredEntries.some(e => e.bodyFat) && (
            <div className="chart-card">
              <div className="chart-header">
                <h3>Body Fat Trend</h3>
              </div>
              <div className="chart-wrapper">
                <Line data={bodyFatChartData} options={chartOptions} />
              </div>
            </div>
          )}

          {/* Measurements Chart */}
          {filteredEntries.some(e => e.measurements && Object.values(e.measurements).some(v => v)) && (
            <div className="chart-card">
              <div className="chart-header">
                <h3>Body Measurements</h3>
              </div>
              <div className="chart-wrapper">
                <Line data={measurementsChartData} options={chartOptions} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-charts">
          <FaChartLine size={64} />
          <p>No data available for the selected time range</p>
          <button className="btn-secondary" onClick={() => setTimeRange('all')}>
            View All Time
          </button>
        </div>
      )}
    </div>
  );
};

export default ProgressDashboard;
